import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectFilesService } from '../api/project-files.service';
import { PROJECT_FOLDER_KEYS, PROJECT_FILE_KEYS } from './queryKeys';
import type {
  CreateProjectFolderInput,
  MoveProjectFolderInput,
  RenameProjectFolderInput,
} from '../types';

export function useProjectFolderMutations(projectId: string) {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useOrganizationStore();

  const orgId = activeOrganizationId ?? '';

  const invalidateFolders = () =>
    queryClient.invalidateQueries({
      queryKey: PROJECT_FOLDER_KEYS.tree(orgId, projectId),
    });

  const invalidateFoldersAndFiles = () => {
    queryClient.invalidateQueries({
      queryKey: PROJECT_FOLDER_KEYS.tree(orgId, projectId),
    });
    queryClient.invalidateQueries({
      queryKey: PROJECT_FILE_KEYS.byProject(orgId, projectId),
    });
  };

  const create = useMutation({
    mutationFn: (data: CreateProjectFolderInput) =>
      projectFilesService.createFolder(projectId, data),
    onSuccess: invalidateFolders,
  });

  const remove = useMutation({
    mutationFn: (folderId: string) => projectFilesService.deleteFolder(projectId, folderId),
    onSuccess: invalidateFoldersAndFiles,
  });

  const rename = useMutation({
    mutationFn: ({ folderId, data }: { folderId: string; data: RenameProjectFolderInput }) =>
      projectFilesService.renameFolder(projectId, folderId, data),
    onSuccess: invalidateFolders,
  });

  const move = useMutation({
    mutationFn: ({ folderId, data }: { folderId: string; data: MoveProjectFolderInput }) =>
      projectFilesService.moveFolder(projectId, folderId, data),
    onSuccess: invalidateFolders,
  });

  return { create, remove, rename, move };
}
